import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FileSpreadsheet, Trophy, TrendingUp, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import EmployeePhoto from './EmployeePhoto';
import ScrollToTop from './ScrollToTop';
import { api } from '../services/api';
import type { RankingsTabProps, Ranking, RoundEvaluatorDTO } from '../types';

const RankingsTab: React.FC<RankingsTabProps> = ({ rounds, userRole, currentUserId }) => {
  const isAdmin = userRole === 'admin';

  // Determine default round: aggregate (admin only), then active round, then latest completed
  const defaultRoundId = useMemo(() => {
    if (isAdmin) return 'aggregate';

    const activeRound = rounds.find(r => r.status === 'active');
    if (activeRound) return activeRound.roundID;

    const completedRounds = rounds
      .filter(r => r.status === 'completed')
      .sort((a, b) => {
        if (a.endDate && b.endDate) return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
        if (a.endDate) return -1;
        if (b.endDate) return 1;
        return b.roundID - a.roundID;
      });
    if (completedRounds.length > 0) return completedRounds[0].roundID;
    return rounds.length > 0 ? rounds[0].roundID : null;
  }, [rounds, isAdmin]);

  const [selectedRoundId, setSelectedRoundId] = useState<number | 'aggregate' | null>(defaultRoundId);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  // Group view for grouped rounds: 'combined' shows all, evaluator ID for a specific group,
  // or 'secondary-{childRoundId}' for secondary evaluator groups
  const [groupView, setGroupView] = useState<'combined' | number | string>('combined');
  const [evaluators, setEvaluators] = useState<RoundEvaluatorDTO[]>([]);
  // Child rounds for the selected grouped round (secondary evaluator rounds)
  const [childRounds, setChildRounds] = useState<import('../types').Round[]>([]);
  // Per-group rankings for the combined view (keyed by label)
  const [combinedGroupRankings, setCombinedGroupRankings] = useState<{ label: string; rankings: Ranking[]; color: string }[]>([]);
  const [combinedLoading, setCombinedLoading] = useState(false);

  // Refs to avoid re-triggering fetch effects when rounds/evaluators arrays change reference
  const roundsRef = useRef(rounds);
  roundsRef.current = rounds;
  const evaluatorsRef = useRef(evaluators);
  evaluatorsRef.current = evaluators;

  // Sync selectedRoundId when rounds load/change and current selection is invalid
  useEffect(() => {
    if (selectedRoundId === null && defaultRoundId !== null) {
      setSelectedRoundId(defaultRoundId);
    }
  }, [defaultRoundId, selectedRoundId]);

  // Fetch evaluators when a grouped round is selected
  useEffect(() => {
    let cancelled = false;
    if (typeof selectedRoundId === 'number') {
      const round = rounds.find(r => r.roundID === selectedRoundId);
      if (round?.isGrouped) {
        api.getRoundEvaluators(selectedRoundId)
          .then(evals => { if (!cancelled) setEvaluators(evals); })
          .catch(() => { if (!cancelled) setEvaluators([]); });
      } else {
        setEvaluators([]);
      }
    } else {
      setEvaluators([]);
    }
    return () => { cancelled = true; };
  }, [selectedRoundId, rounds]);

  // Fetch child rounds when a grouped round is selected
  useEffect(() => {
    let cancelled = false;
    if (typeof selectedRoundId === 'number') {
      const round = rounds.find(r => r.roundID === selectedRoundId);
      if (round?.isGrouped && !round.isSecondary) {
        // Check if child rounds are already in the rounds array
        const inlineChildren = rounds.filter(r => r.isSecondary && r.parentRoundID === selectedRoundId);
        if (inlineChildren.length > 0) {
          setChildRounds(inlineChildren);
        } else {
          api.getChildRounds(selectedRoundId)
            .then(children => { if (!cancelled) setChildRounds(children); })
            .catch(() => { if (!cancelled) setChildRounds([]); });
        }
      } else {
        setChildRounds([]);
      }
    } else {
      setChildRounds([]);
    }
    return () => { cancelled = true; };
  }, [selectedRoundId, rounds]);

  // Fetch per-group rankings for combined view on grouped rounds
  useEffect(() => {
    let cancelled = false;
    const selectedRound = typeof selectedRoundId === 'number' ? rounds.find(r => r.roundID === selectedRoundId) : null;
    const canSeeGroups = isAdmin || evaluators.some(e => e.employeeID === currentUserId && e.isFinalReviewer);

    if (groupView === 'combined' && selectedRound?.isGrouped && canSeeGroups && evaluators.length > 0) {
      setCombinedLoading(true);
      const primaryEvals = evaluators.filter(e => !e.isFinalReviewer);

      const fetchAll = async () => {
        const groups: { label: string; rankings: Ranking[]; color: string }[] = [];

        // Fetch each primary evaluator group
        await Promise.all(primaryEvals.map(async (ev) => {
          try {
            const data = await api.getRankings(selectedRoundId as number, ev.employeeID);
            groups.push({ label: `${ev.name}'s Group`, rankings: data, color: 'purple' });
          } catch { /* skip */ }
        }));

        // Fetch each secondary (child round) group
        await Promise.all(childRounds.map(async (child) => {
          try {
            const data = await api.getRankings(child.roundID);
            const match = child.roundName.match(/- (.+)\s*→\s*(.+)$/);
            const label = match ? `${match[2].trim()} (for ${match[1].trim()})` : child.roundName;
            groups.push({ label, rankings: data, color: 'amber' });
          } catch { /* skip */ }
        }));

        if (!cancelled) {
          // Sort: primary groups first (in evaluator order), then secondary
          const primaryLabels = primaryEvals.map(ev => `${ev.name}'s Group`);
          groups.sort((a, b) => {
            const aIdx = primaryLabels.indexOf(a.label);
            const bIdx = primaryLabels.indexOf(b.label);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;
            return 0;
          });
          setCombinedGroupRankings(groups);
          setCombinedLoading(false);
        }
      };
      fetchAll();
    } else {
      setCombinedGroupRankings([]);
    }
    return () => { cancelled = true; };
  }, [groupView, selectedRoundId, rounds, evaluators, childRounds, isAdmin, currentUserId]);

  // Fetch rankings when selection or group view changes
  useEffect(() => {
    if (selectedRoundId === null) {
      setRankings([]);
      return;
    }
    // Only admins may call aggregate rankings
    if (selectedRoundId === 'aggregate' && !isAdmin) {
      setRankings([]);
      return;
    }
    let cancelled = false;
    const fetchRankings = async () => {
      setLoading(true);
      try {
        let data: Ranking[];
        if (selectedRoundId === 'aggregate') {
          data = await api.getAggregateRankings();
        } else if (typeof groupView === 'string' && groupView.startsWith('secondary-')) {
          // Secondary evaluator group: fetch from child round
          const childRoundId = Number(groupView.replace('secondary-', ''));
          data = await api.getRankings(childRoundId);
        } else {
          const round = roundsRef.current.find(r => r.roundID === selectedRoundId);
          let evalId: number | undefined;
          if (round?.isGrouped) {
            const isFR = evaluatorsRef.current.some(e => e.employeeID === currentUserId && e.isFinalReviewer);
            if (isAdmin || isFR) {
              // Admin/FR: 'combined' = no filter; specific evaluator ID = group filter
              evalId = groupView === 'combined' ? undefined : (typeof groupView === 'number' ? groupView : undefined);
            } else {
              // Regular evaluator: always show their own group
              evalId = currentUserId;
            }
          }
          data = await api.getRankings(selectedRoundId as number, evalId);
        }
        if (!cancelled) setRankings(data);
      } catch (err) {
        console.error('Error fetching rankings:', err);
        if (!cancelled) setRankings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRankings();
    return () => { cancelled = true; };
  }, [selectedRoundId, groupView, isAdmin, currentUserId]);

  const selectedRound = typeof selectedRoundId === 'number'
    ? rounds.find(r => r.roundID === selectedRoundId)
    : null;

  // Extract unique managers and departments from rankings
  const uniqueManagers = useMemo(() => {
    const managers = rankings
      .map(r => r.manager)
      .filter((m, idx, arr) => m && arr.indexOf(m) === idx)
      .sort();
    return managers;
  }, [rankings]);

  const uniqueDepartments = useMemo(() => {
    const departments = rankings
      .map(r => r.department)
      .filter((d, idx, arr) => d && arr.indexOf(d) === idx)
      .sort();
    return departments;
  }, [rankings]);

  // Filter rankings based on selected filters
  const filteredRankings = useMemo(() => {
    let filtered = rankings;
    if (selectedManager) {
      filtered = filtered.filter(r => r.manager === selectedManager);
    }
    if (selectedDepartment) {
      filtered = filtered.filter(r => r.department === selectedDepartment);
    }
    return filtered;
  }, [rankings, selectedManager, selectedDepartment]);

  const getExportLabel = () => {
    if (selectedRoundId === 'aggregate') return 'Aggregate';
    const roundName = selectedRound?.roundName || 'Round';
    if (typeof groupView === 'string' && groupView.startsWith('secondary-')) {
      const childId = Number(groupView.replace('secondary-', ''));
      const child = childRounds.find(c => c.roundID === childId);
      const match = child?.roundName.match(/- (.+)\s*→\s*(.+)$/);
      return match ? `${roundName}-Secondary-${match[2].trim()}` : `${roundName}-Secondary`;
    }
    if (groupView === 'combined') return selectedRound?.isGrouped ? `${roundName}-Combined` : roundName;
    if (typeof groupView === 'number') {
      const evalName = evaluators.find(e => e.employeeID === groupView)?.name || 'Group';
      return `${roundName}-${evalName}`;
    }
    return roundName;
  };

  const handleExport = () => {
    const exportLabel = getExportLabel();
    const header = ['Rank', 'Name', 'Title', 'Department', 'Manager', 'Elo Rating', 'Comparisons', 'Wins', 'Losses'];
    const formatRow = (r: Ranking) => [
      r.rank, r.name, r.title, r.department, r.manager,
      r.eloRating, r.totalComparisons, r.wins, r.losses
    ];

    const toSheetData = (data: Ranking[]) => [header, ...data.map(formatRow)];

    if (groupView === 'combined' && selectedRound?.isGrouped && combinedGroupRankings.length > 0) {
      // Multi-sheet Excel: Sheet1 = Combined, then one sheet per group
      const wb = XLSX.utils.book_new();

      // Sheet 1: Combined (all rankings merged)
      const combinedSheet = XLSX.utils.aoa_to_sheet(toSheetData(filteredRankings));
      XLSX.utils.book_append_sheet(wb, combinedSheet, 'Combined');

      // Individual sheets per group
      combinedGroupRankings.forEach(group => {
        const sheetName = group.label.length > 31 ? group.label.substring(0, 31) : group.label;
        const ws = XLSX.utils.aoa_to_sheet(toSheetData(group.rankings));
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(wb, `${exportLabel}-Rankings.xlsx`);
    } else {
      // Single sheet export
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(toSheetData(filteredRankings));
      XLSX.utils.book_append_sheet(wb, ws, 'Rankings');
      XLSX.writeFile(wb, `${exportLabel}-Rankings.xlsx`);
    }

    // Log export event
    api.logDataExport(
      `Exported rankings: ${exportLabel}`,
      'Rankings'
    ).catch(err => console.error('Failed to log export event:', err));
  };

  /* Mobile: card-based ranking list */
  const renderRankingsCards = (data: Ranking[]) => (
    <div className="space-y-2">
      {data.map((emp) => (
        <div
          key={emp.employeeID}
          className={`flex items-center gap-3 p-3 rounded-xl border ${
            emp.rank === 1
              ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300'
              : emp.rank === 2
              ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300'
              : emp.rank === 3
              ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300'
              : 'bg-white border-gray-200'
          }`}
        >
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm flex-shrink-0 ${
              emp.rank === 1
                ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-yellow-900'
                : emp.rank === 2
                ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-900'
                : emp.rank === 3
                ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-orange-900'
                : 'bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            {emp.rank}
          </span>
          <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{emp.name}</p>
            <p className="text-xs text-gray-500 truncate">{emp.title}{emp.department ? ` · ${emp.department}` : ''}</p>
            {emp.manager && <p className="text-xs text-gray-400 truncate">{emp.manager}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
              {emp.eloRating}
            </p>
            <p className="text-[10px] text-gray-400">Elo</p>
          </div>
        </div>
      ))}
    </div>
  );

  /* Desktop: full table layout */
  const renderRankingsTable = (data: Ranking[]) => (
    <table className="w-full table-fixed" style={{ minWidth: '700px' }}>
      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
        <tr>
          <th className="w-[8%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Rank</th>
          <th className="w-[18%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Employee</th>
          <th className="w-[22%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Title</th>
          <th className="w-[16%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department</th>
          <th className="w-[16%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Manager</th>
          <th className="w-[10%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Elo Rating</th>
          <th className="w-[10%] px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Comparisons</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {data.map((emp) => (
          <tr
            key={emp.employeeID}
            className={`hover:bg-gray-50 transition-colors ${emp.rank <= 3 ? 'bg-gradient-to-r' : ''
              } ${emp.rank === 1
                ? 'from-yellow-50 to-yellow-50/30'
                : emp.rank === 2
                  ? 'from-gray-50 to-gray-50/30'
                  : emp.rank === 3
                    ? 'from-orange-50 to-orange-50/30'
                    : ''
              }`}
          >
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-base shadow-md ${emp.rank === 1
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-yellow-900'
                      : emp.rank === 2
                        ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-900'
                        : emp.rank === 3
                          ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-orange-900'
                          : 'bg-gray-100 text-gray-700 border-2 border-gray-200'
                    }`}
                >
                  {emp.rank}
                </span>
                {emp.rank <= 3 && (
                  <Trophy
                    className={`w-5 h-5 ${emp.rank === 1
                        ? 'text-yellow-500'
                        : emp.rank === 2
                          ? 'text-gray-400'
                          : 'text-orange-500'
                      }`}
                  />
                )}
              </div>
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                <span className="font-semibold text-gray-900 truncate" title={emp.name}>{emp.name}</span>
              </div>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-700 font-medium block truncate" title={emp.title}>{emp.title}</span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 block truncate" title={emp.department}>{emp.department}</span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 block truncate" title={emp.manager}>{emp.manager}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className="font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {emp.eloRating}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 font-medium">{emp.totalComparisons}</span>
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Responsive: header stacks on mobile */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Rankings</h2>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              {selectedRoundId === 'aggregate'
                ? 'Showing Aggregate results across all rounds'
                : typeof groupView === 'string' && groupView.startsWith('secondary-')
                ? (() => {
                    const childId = Number(groupView.replace('secondary-', ''));
                    const child = childRounds.find(c => c.roundID === childId);
                    const match = child?.roundName.match(/- (.+)\s*→\s*(.+)$/);
                    const label = match ? `${match[2].trim()} (secondary for ${match[1].trim()})` : child?.roundName || 'secondary';
                    return `Showing secondary evaluation results: ${label}`;
                  })()
                : selectedRound?.isGrouped && groupView !== 'combined'
                ? `Showing group results for ${evaluators.find(e => e.employeeID === groupView)?.name || 'evaluator'} in ${selectedRound.roundName}`
                : `Showing ${selectedRound?.isGrouped ? 'combined ' : ''}results for ${selectedRound?.roundName || 'selected round'}`}
            </p>
          </div>
          {/* Responsive: controls wrap on mobile */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedRoundId === 'aggregate' ? 'aggregate' : selectedRoundId ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedRoundId(val === 'aggregate' ? 'aggregate' : Number(val));
                  setGroupView('combined');
                  setSelectedManager('');
                  setSelectedDepartment('');
                }}
                className="appearance-none pl-4 pr-10 py-3 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer w-full sm:min-w-[220px] min-h-[44px]"
              >
                {isAdmin && <option value="aggregate">Aggregate (All Rounds)</option>}
                {rounds.map(round => (
                  <option key={round.roundID} value={round.roundID}>
                    {round.roundName} ({round.status})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {isAdmin && (
              <button
                onClick={handleExport}
                disabled={filteredRankings.length === 0 && combinedGroupRankings.length === 0}
                className="flex items-center gap-2 px-4 sm:px-5 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                <FileSpreadsheet className="w-5 h-5" />
                Export to Excel
              </button>
            )}
          </div>
        </div>

        {/* Group Tabs for grouped rounds */}
        {(isAdmin || evaluators.some(e => e.employeeID === currentUserId && e.isFinalReviewer)) && selectedRound?.isGrouped && evaluators.length > 0 && (
          <div className="border-b border-gray-200">
            <nav className="flex gap-0 -mb-px overflow-x-auto" role="tablist">
              <button
                role="tab"
                aria-selected={groupView === 'combined'}
                onClick={() => setGroupView('combined')}
                className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                  groupView === 'combined'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Combined
              </button>
              {evaluators.filter(e => !e.isFinalReviewer).map(evaluator => (
                <button
                  key={evaluator.employeeID}
                  role="tab"
                  aria-selected={groupView === evaluator.employeeID}
                  onClick={() => setGroupView(evaluator.employeeID)}
                  className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    groupView === evaluator.employeeID
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {evaluator.name}'s Group
                </button>
              ))}
              {childRounds.map(child => {
                const key = `secondary-${child.roundID}`;
                const match = child.roundName.match(/- (.+)\s*→\s*(.+)$/);
                const label = match ? `${match[2].trim()} (for ${match[1].trim()})` : child.roundName;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={groupView === key}
                    onClick={() => setGroupView(key)}
                    className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                      groupView === key
                        ? 'border-amber-600 text-amber-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Responsive: filter row wraps on mobile */}
        {rankings.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative w-full sm:w-auto sm:min-w-[220px]">
              <select
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                className="appearance-none w-full pl-4 pr-10 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer min-h-[44px]"
              >
                <option value="">All Managers</option>
                {uniqueManagers.map(manager => (
                  <option key={manager} value={manager}>
                    {manager}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative w-full sm:w-auto sm:min-w-[220px]">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="appearance-none w-full pl-4 pr-10 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer min-h-[44px]"
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map(department => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {(selectedManager || selectedDepartment) && (
              <button
                onClick={() => {
                  setSelectedManager('');
                  setSelectedDepartment('');
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
              >
                Clear Filters
              </button>
            )}

            <div className="w-full sm:w-auto sm:ml-auto text-sm text-gray-600 font-medium">
              Showing {filteredRankings.length} of {rankings.length} employees
            </div>
          </div>
        )}
      </div>

      {(loading || combinedLoading) && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading rankings...</p>
        </div>
      )}

      {/* Mobile: card layout; Desktop: table layout */}
      {!loading && !combinedLoading && rankings.length > 0 && (
        <>
          <div className="md:hidden">
            {renderRankingsCards(filteredRankings)}
          </div>
          <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            {renderRankingsTable(filteredRankings)}
          </div>
        </>
      )}

      {!loading && !combinedLoading && rankings.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
          <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Rankings Available</h3>
          <p className="text-gray-600">
            {selectedRoundId === 'aggregate'
              ? 'Start comparing employees to generate rankings'
              : 'No comparisons have been completed in this round yet'}
          </p>
        </div>
      )}
      <ScrollToTop />
    </div>
  );
};

export default RankingsTab;
